import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import {
  FolderKanban, Briefcase, Construction, Search, Wallet, CheckCircle2,
  FolderPlus, FilePlus2, FolderOpen, CalendarClock, Send, XCircle,
  Archive, UserPlus, Activity as ActivityIcon, Users, Building2, IdCard,
  ShieldCheck, PieChart as PieChartIcon, BarChart3, Inbox,
} from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import { departmentsApi } from '../../api/admin/departments';
import { positionsApi } from '../../api/admin/positions';
import { rolesApi } from '../../api/admin/roles';
import { usersApi } from '../../api/admin/users';
import { projectsApi } from '../../api/projects';
import { programmesApi } from '../../api/programmes';
import { masterDataApi } from '../../api/masterData';
import { formatRelativeTime, daysRemaining } from '../../lib/format';

const STATUS_COLORS: Record<string, string> = {
  draft: '#9ca3af', submitted: '#f59e0b', under_review: '#7c3aed', returned: '#f97316',
  clarification_required: '#f97316', approved: '#3b82f6', rejected: '#dc2626', cancelled: '#6b7280',
  active: '#3b82f6', in_progress: '#0891b2', completed: '#15803d', closed: '#4b5563', archived: '#9ca3af',
};
const SECTOR_COLORS = ['#1d4ed8', '#15803d', '#b45309', '#7c3aed', '#be185d', '#0891b2', '#65a30d', '#c2410c'];

const ACTIVITY_ICONS: Record<string, typeof Send> = {
  submitted: Send, approved: CheckCircle2, rejected: XCircle, closed: Archive, created: UserPlus,
};

/**
 * Visual refinement pass only - every data source, endpoint, and
 * business rule below is unchanged from the previously verified
 * dashboard. All figures come from real API calls; every empty state
 * is a deliberate message, never a fabricated number or chart.
 *
 * Portfolio Budget's "Committed vs Available" split is a real,
 * derived definition (not a separately stored figure): Committed =
 * sum(budget_total) for approved/in_progress/completed projects;
 * Available = the remaining sum across everything else still in the
 * pipeline. Documented here so the definition is clear.
 */
export function DashboardPage() {
  const { user } = useAuth();

  const departments = useQuery({ queryKey: ['departments'], queryFn: () => departmentsApi.list() });
  const positions = useQuery({ queryKey: ['positions'], queryFn: () => positionsApi.list() });
  const roles = useQuery({ queryKey: ['roles'], queryFn: () => rolesApi.list() });
  const users = useQuery({ queryKey: ['users'], queryFn: () => usersApi.list() });
  const projects = useQuery({ queryKey: ['projects'], queryFn: () => projectsApi.list() });
  const programmes = useQuery({ queryKey: ['programmes'], queryFn: () => programmesApi.list() });
  const sectors = useQuery({ queryKey: ['sectors'], queryFn: () => masterDataApi.sectors() });
  const pendingApprovals = useQuery({ queryKey: ['pending-approvals'], queryFn: () => projectsApi.pendingApprovals() });
  const recentActivity = useQuery({ queryKey: ['recent-activity'], queryFn: () => projectsApi.recentActivity() });

  const activeDepartments = departments.data?.data.filter((d) => d.is_active).length;
  const activePositions = positions.data?.data.filter((p) => p.is_active).length;
  const activeRoles = roles.data?.data.filter((r) => r.is_active).length;
  const activeUsers = users.data?.data.filter((u) => u.is_active).length;

  const allProjects = projects.data?.data ?? [];
  const totalProjects = allProjects.length;
  const projectsInProgress = allProjects.filter((p) => p.status === 'in_progress').length;
  const projectsUnderReview = allProjects.filter((p) => p.status === 'under_review').length;
  const committedBudget = allProjects
    .filter((p) => ['approved', 'in_progress', 'completed'].includes(p.status))
    .reduce((sum, p) => sum + Number(p.budget_total), 0);
  const totalPortfolioBudget = allProjects.reduce((sum, p) => sum + Number(p.budget_total), 0);
  const availableBudget = Math.max(totalPortfolioBudget - committedBudget, 0);
  const committedPercent = totalPortfolioBudget > 0 ? Math.round((committedBudget / totalPortfolioBudget) * 100) : 0;
  const totalProgrammes = programmes.data?.data.length;

  const statusCounts = new Map<string, number>();
  allProjects.forEach((p) => statusCounts.set(p.status, (statusCounts.get(p.status) ?? 0) + 1));
  const statusChartData = Array.from(statusCounts.entries()).map(([status, count]) => ({ status, count }));

  const sectorNameById = new Map(sectors.data?.data.map((s) => [s.id, s.name]) ?? []);
  const sectorCounts = new Map<string, number>();
  allProjects.forEach((p) => {
    const name = sectorNameById.get(p.sector_id);
    if (name) sectorCounts.set(name, (sectorCounts.get(name) ?? 0) + 1);
  });
  const sectorChartData = Array.from(sectorCounts.entries()).map(([name, count]) => ({ name, count }));

  const now = new Date();
  const thirtyDaysOut = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  const upcomingDeadlines = allProjects
    .filter((p) => ['approved', 'in_progress'].includes(p.status))
    .filter((p) => new Date(p.end_date) >= now && new Date(p.end_date) <= thirtyDaysOut)
    .sort((a, b) => new Date(a.end_date).getTime() - new Date(b.end_date).getTime());

  const sevenDaysOut = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  const urgentDeadlines = upcomingDeadlines.filter((p) => new Date(p.end_date) <= sevenDaysOut);

  const isLoadingCore = projects.isLoading || programmes.isLoading;

  return (
    <div className="page">
      <div className="dashboard-header">
        <div>
          <h1>Dashboard</h1>
          <p className="muted">Welcome back, {user?.fullName?.split(' ')[0]}. Here's what's happening across your programmes and projects.</p>
        </div>
      </div>

      {(pendingApprovals.data && pendingApprovals.data.data.length > 0) || urgentDeadlines.length > 0 ? (
        <section className="card alert-card">
          <h2>Alerts &amp; Pending Actions</h2>
          <ul className="alert-list">
            {pendingApprovals.data && pendingApprovals.data.data.length > 0 && (
              <li>{pendingApprovals.data.data.length} project{pendingApprovals.data.data.length === 1 ? '' : 's'} awaiting your approval.</li>
            )}
            {urgentDeadlines.length > 0 && (
              <li>{urgentDeadlines.length} project{urgentDeadlines.length === 1 ? '' : 's'} ending within 7 days.</li>
            )}
          </ul>
        </section>
      ) : null}

      {/* Section 1: KPI cards */}
      <div className="kpi-grid">
        <KpiCard icon={<FolderKanban size={20} />} color="#eef2ff" iconColor="#4338ca" label="Total Programmes" value={totalProgrammes} loading={programmes.isLoading} />
        <KpiCard icon={<Briefcase size={20} />} color="#eff6ff" iconColor="#1d4ed8" label="Total Projects" value={totalProjects} loading={projects.isLoading} />
        <KpiCard icon={<Wallet size={20} />} color="#f5f3ff" iconColor="#7c3aed" label="Total Portfolio Budget" value={totalPortfolioBudget ? `₦${Math.round(totalPortfolioBudget).toLocaleString()}` : undefined} loading={projects.isLoading} />
        <KpiCard icon={<Construction size={20} />} color="#ecfeff" iconColor="#0891b2" label="In Progress" value={projectsInProgress} loading={projects.isLoading} />
        <KpiCard icon={<Search size={20} />} color="#fffbeb" iconColor="#b45309" label="Awaiting Review" value={projectsUnderReview} loading={projects.isLoading} />
        <KpiCard icon={<CheckCircle2 size={20} />} color="#f0fdf4" iconColor="#15803d" label="Pending Your Approval" value={pendingApprovals.data?.data.length} loading={pendingApprovals.isLoading} />
      </div>

      {/* Section 2: Projects by Status + Portfolio Budget */}
      <div className="dashboard-chart-row">
        <section className="card">
          <h2>Projects by Status</h2>
          {isLoadingCore ? (
            <div className="chart-empty-state"><p className="muted">Loading…</p></div>
          ) : totalProjects === 0 ? (
            <EmptyState icon={<PieChartIcon size={28} />} message="No projects yet" />
          ) : (
            <>
              <div className="donut-wrapper">
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={statusChartData} dataKey="count" nameKey="status" cx="50%" cy="50%" innerRadius={65} outerRadius={95} paddingAngle={2}>
                      {statusChartData.map((entry) => <Cell key={entry.status} fill={STATUS_COLORS[entry.status] ?? '#9ca3af'} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="donut-center-label">
                  <div className="donut-center-value">{totalProjects}</div>
                  <div className="donut-center-caption">Total</div>
                </div>
              </div>
              <ul className="legend-list">
                {statusChartData.map((entry) => (
                  <li key={entry.status}>
                    <span className="legend-dot" style={{ background: STATUS_COLORS[entry.status] ?? '#9ca3af' }} />
                    {entry.status.replace('_', ' ')} - {entry.count}
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>

        <section className="card">
          <h2>Portfolio Budget</h2>
          {isLoadingCore ? (
            <div className="chart-empty-state"><p className="muted">Loading…</p></div>
          ) : totalPortfolioBudget === 0 ? (
            <EmptyState icon={<Wallet size={28} />} message="No portfolio budget yet" />
          ) : (
            <>
              <div className="donut-wrapper">
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={[{ name: 'Committed', value: committedBudget }, { name: 'Available', value: availableBudget }]}
                      dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={65} outerRadius={95} paddingAngle={2}
                    >
                      <Cell fill="#15803d" />
                      <Cell fill="#d1d5db" />
                    </Pie>
                    <Tooltip formatter={(value: number) => `₦${Math.round(value).toLocaleString()}`} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="donut-center-label">
                  <div className="donut-center-value">{committedPercent}%</div>
                  <div className="donut-center-caption">Committed</div>
                </div>
              </div>
              <ul className="legend-list">
                <li><span className="legend-dot" style={{ background: '#15803d' }} /> Committed - ₦{Math.round(committedBudget).toLocaleString()}</li>
                <li><span className="legend-dot" style={{ background: '#d1d5db' }} /> Available - ₦{Math.round(availableBudget).toLocaleString()}</li>
              </ul>
            </>
          )}
        </section>
      </div>

      {/* Section 3: Projects by Sector + Quick Actions */}
      <div className="dashboard-chart-row">
        <section className="card">
          <h2>Projects by Sector</h2>
          {isLoadingCore ? (
            <div className="chart-empty-state"><p className="muted">Loading…</p></div>
          ) : sectorChartData.length === 0 ? (
            <EmptyState icon={<BarChart3 size={28} />} message="No project data yet" />
          ) : (
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={Math.max(sectorChartData.length * 40, 140)}>
                <BarChart data={sectorChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={110} />
                  <Tooltip />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {sectorChartData.map((_, i) => <Cell key={i} fill={SECTOR_COLORS[i % SECTOR_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        <section className="card">
          <h2>Quick Actions</h2>
          <div className="quick-actions-grid">
            <Link className="quick-action" to="/programmes?new=1">
              <span className="quick-action-icon"><FolderPlus size={18} /></span>
              <div><strong>New Programme</strong><div className="muted">Create new programme</div></div>
            </Link>
            <Link className="quick-action" to="/projects?new=1">
              <span className="quick-action-icon"><FilePlus2 size={18} /></span>
              <div><strong>New Project</strong><div className="muted">Create new project</div></div>
            </Link>
            <Link className="quick-action" to="/projects">
              <span className="quick-action-icon"><Briefcase size={18} /></span>
              <div><strong>View Projects</strong><div className="muted">Browse all projects</div></div>
            </Link>
            <Link className="quick-action" to="/programmes">
              <span className="quick-action-icon"><FolderOpen size={18} /></span>
              <div><strong>View Programmes</strong><div className="muted">Browse all programmes</div></div>
            </Link>
          </div>
        </section>
      </div>

      {/* Section 4: Upcoming Deadlines + Pending Approvals */}
      <div className="dashboard-chart-row">
        <section className="card">
          <h2>Upcoming Deadlines</h2>
          {upcomingDeadlines.length === 0 ? (
            <EmptyState icon={<CalendarClock size={28} />} message="No active projects ending in the next 30 days." small />
          ) : (
            <ul className="deadline-list">
              {upcomingDeadlines.map((p) => {
                const days = daysRemaining(p.end_date);
                return (
                  <li key={p.id} className="deadline-item">
                    <CalendarClock size={16} className="inline-icon" />
                    <div>
                      <div><strong>{p.project_name}</strong> <span className="muted">({p.project_reference})</span></div>
                      <div className="muted">{p.end_date}{days !== null && <span className={days <= 7 ? 'deadline-urgent' : ''}> · {days === 0 ? 'Due today' : `${days} day${days === 1 ? '' : 's'} remaining`}</span>}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="card">
          <h2>Pending Approvals</h2>
          {pendingApprovals.isLoading ? (
            <div className="chart-empty-state"><p className="muted">Loading…</p></div>
          ) : pendingApprovals.data && pendingApprovals.data.data.length === 0 ? (
            <EmptyState icon={<Inbox size={28} />} message="Nothing awaiting your approval right now." small />
          ) : (
            <ul className="deadline-list">
              {pendingApprovals.data?.data.map((a) => (
                <li key={a.id} className="approval-item">
                  <div>
                    <strong>{a.project_name}</strong> <span className="muted">({a.project_reference})</span>
                    <div className="muted">
                      Project approval · sequence {a.sequence}
                      {a.budget_total && a.currency_code && ` · ${a.currency_code} ${Number(a.budget_total).toLocaleString()}`}
                      {' · '}Submitted {formatRelativeTime(a.created_at)}
                    </div>
                  </div>
                  <Link className="link-button" to={`/projects/${a.record_id}`}>Review</Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* Section 5: Recent Project Activity */}
      <section className="card">
        <h2>Recent Project Activity</h2>
        {recentActivity.isLoading ? (
          <p className="muted">Loading…</p>
        ) : recentActivity.data && recentActivity.data.data.length === 0 ? (
          <EmptyState icon={<ActivityIcon size={28} />} message="No project status changes yet." small />
        ) : (
          <ul className="deadline-list">
            {recentActivity.data?.data.map((t) => {
              const Icon = ACTIVITY_ICONS[t.to_state] ?? ActivityIcon;
              return (
                <li key={t.id} className="activity-item">
                  <Icon size={15} className="inline-icon" />
                  <span>{t.project_name} <span className="muted">({t.project_reference})</span>: {t.from_state} → {t.to_state}</span>
                  <span className="muted activity-time">{formatRelativeTime(t.created_at)}</span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Deferred module placeholders - visually consistent, clearly future areas */}
      <div className="dashboard-chart-row">
        <section className="card placeholder-card">
          <h2><Construction size={16} className="inline-icon" /> Workplan &amp; Activity Progress</h2>
          <p className="muted">Available when the Activities and Workplan module is introduced.</p>
        </section>
        <section className="card placeholder-card">
          <h2><BarChart3 size={16} className="inline-icon" /> Outcome &amp; Output Performance</h2>
          <p className="muted">Available when the Logical Framework module is introduced.</p>
        </section>
      </div>

      {/* Section 6: Administration Overview */}
      <section className="card">
        <h2>Administration Overview</h2>
        <div className="admin-overview-grid">
          <AdminStat icon={<Users size={18} />} label="Active Users" value={activeUsers} loading={users.isLoading} />
          <AdminStat icon={<Building2 size={18} />} label="Active Departments" value={activeDepartments} loading={departments.isLoading} />
          <AdminStat icon={<IdCard size={18} />} label="Active Positions" value={activePositions} loading={positions.isLoading} />
          <AdminStat icon={<ShieldCheck size={18} />} label="Active Roles" value={activeRoles} loading={roles.isLoading} />
        </div>
      </section>
    </div>
  );
}

function KpiCard({ icon, color, iconColor, label, value, loading }: { icon: React.ReactNode; color: string; iconColor: string; label: string; value: number | string | undefined; loading: boolean }) {
  return (
    <div className="kpi-card">
      <div className="kpi-icon" style={{ background: color, color: iconColor }}>{icon}</div>
      <div>
        <div className="kpi-value">{loading ? '…' : value ?? '-'}</div>
        <div className="kpi-label">{label}</div>
      </div>
    </div>
  );
}

function AdminStat({ icon, label, value, loading }: { icon: React.ReactNode; label: string; value: number | undefined; loading: boolean }) {
  return (
    <div className="admin-stat">
      <span className="admin-stat-icon">{icon}</span>
      <div>
        <div className="admin-stat-value">{loading ? '…' : value ?? '-'}</div>
        <div className="admin-stat-label">{label}</div>
      </div>
    </div>
  );
}

function EmptyState({ icon, message, small }: { icon: React.ReactNode; message: string; small?: boolean }) {
  return (
    <div className={small ? 'empty-state-inline' : 'chart-empty-state'}>
      <div className="empty-state-icon">{icon}</div>
      <p className="muted">{message}</p>
    </div>
  );
}
